import { FieldValues, Path, UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useEffect } from "react";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { cn } from "@/lib/utils";
import { useActiveLocationsStore } from "@/stores/useActiveLocationsStore";
import { MultiSelectCombobox } from "./MultiSelectCombobox";

interface LocationMultiSelectProps<T extends FieldValues> {
  form: UseFormReturn<T>;
  name: Path<T>;
  label: string;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  disabled?: boolean;
  className?: string;
}

export function LocationMultiSelect<T extends FieldValues>({
  form,
  name,
  label,
  placeholder = "locations.select_locations",
  searchPlaceholder = "locations.search_locations",
  emptyMessage = "locations.no_location_found",
  disabled = false,
  className,
}: LocationMultiSelectProps<T>) {
  const { t } = useTranslation();
  const { availableLocations } = useActiveLocationsStore();

  const isSingleLocation = availableLocations.length === 1;

  // Auto-select single location
  useEffect(() => {
    if (isSingleLocation && availableLocations[0]) {
      const currentValue = form.getValues(name) as string[];
      if (!currentValue || currentValue.length === 0) {
        form.setValue(name, [availableLocations[0].locationId] as any);
      }
    }
  }, [isSingleLocation, availableLocations, form, name]);

  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => {
        const selectedIds = (field.value as string[]) || [];

        // Transform locations to options format
        const options = availableLocations.map((location) => ({
          id: location.locationId,
          label: location.name,
        }));

        return (
          <FormItem className={cn("w-full", className)}>
            <FormLabel>{t(label)}</FormLabel>
            <FormControl>
              <MultiSelectCombobox
                options={options}
                selectedIds={selectedIds}
                onSelectionChange={(newIds) => field.onChange(newIds)}
                placeholder={t(placeholder)}
                searchPlaceholder={t(searchPlaceholder)}
                emptyMessage={t(emptyMessage)}
                disabled={disabled || isSingleLocation}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
}
